import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";

import NotificationsIcon from "@mui/icons-material/Notifications";
import EmailIcon from "@mui/icons-material/Email";

import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import ConfigSwitchCard from "../../styled/ConfigSwitchCard";

const configOptions = [
  {
    id: "send_cashier_closing_email",
    title: "Email de Fechamento de Caixa",
    description: {
      enabled:
        "Ativado: Um email com o resumo do fechamento de caixa sera enviado ao proprietario ao fechar o caixa.",
      disabled:
        "Desativado: Nenhum email sera enviado ao fechar o caixa.",
    },
    icon: EmailIcon,
    successMessage: (value) =>
      `Email de fechamento ${value ? "ativado" : "desativado"} com sucesso`,
    errorMessage: "Erro ao atualizar configuracao de email de fechamento",
    notificationId: "cashier-closing-email-config",
  },
];

export default function NotificationsConfigModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const configParams = configOptions.map((option) => option.id).join(",");
      const params = { config: configParams };
      const { data } = await getDeliveryCenterConfig(params);

      setConfigValues(data);
    } catch (err) {
      console.log(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configuracoes. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "notifications-configs-load-error",
        "Erro ao carregar configuracoes",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    try {
      const newValue = event.target.checked;
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      if (!configOption) return;

      const data = {
        config: {
          [configId]: newValue,
        },
      };

      await setDeliveryCenterConfig(data);

      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValue,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        configOption.successMessage(newValue),
        "success"
      );
    } catch (e) {
      console.error(e);
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      dispatchSnackBarMessage(
        `${configId}-update-error`,
        configOption?.errorMessage || "Erro ao atualizar configuracao",
        "error"
      );
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Notificacoes"
        icon={NotificationsIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            configOptions.map((config) => {
              const isChecked = configValues[config.id] ?? true;

              return (
                <ConfigSwitchCard
                  key={config.id}
                  icon={config.icon}
                  title={config.title}
                  description={
                    isChecked
                      ? config.description.enabled
                      : config.description.disabled
                  }
                  isActive={isChecked}
                  onChange={(e) => handleConfigChange(config.id, e)}
                />
              );
            })
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
